import { useCallback } from 'react';
import { EventPartScore, PublishedEventListItem, Scoreboard } from '../@types';
import eventListItemUpdater from '../helpers/eventListItemUpdater';
import { MessageType } from './useWebsocket';
import { MessageBetType } from '../helpers/betslipMessageParser';

type PublishedEventWithScores = PublishedEventListItem & {
  eventPartScores?: EventPartScore[];
  mainEventPart?: EventPartScore | null;
};

export const useUpdaterCallback = (
  setEvents: React.Dispatch<React.SetStateAction<PublishedEventListItem[]>>,
  setSelectedEvent: React.Dispatch<React.SetStateAction<PublishedEventListItem | null>>,
  selectedEventRef: React.MutableRefObject<PublishedEventListItem | null>,
  scoreboardStateRef: React.MutableRefObject<Scoreboard | null>,
  setScoreboardState: React.Dispatch<React.SetStateAction<Scoreboard | null>>,
  refetchScoreboard: () => void
) => {
  return useCallback(
    (data: Record<string, MessageType<MessageBetType>>[]) => {
      const messagesByEvent = new Map<string, MessageType<MessageBetType>[]>();

      data.forEach((message) => {
        const eventId = Object.keys(message)[0];
        const messageData = message[eventId];

        if (!messagesByEvent.has(eventId)) {
          messagesByEvent.set(eventId, []);
        }

        const messages = messagesByEvent.get(eventId);
        if (messages) {
          messages.push(messageData);
        }
      });

      let scoreboardUpdate: Partial<Scoreboard> | null = null;

      setEvents((prevState) => {
        if (!prevState) return prevState;
        let hasEventChanges = false;

        const updatedEvents = prevState.map((event) => {
          const eventMessages = messagesByEvent.get(event.id);
          if (!eventMessages) return event;

          const isSelectedEvent = selectedEventRef.current?.id === event.id;

          let updatedEvent: PublishedEventWithScores = event;
          for (const messageData of eventMessages) {
            updatedEvent = eventListItemUpdater(
              {
                ...updatedEvent,
                eventPartScores: isSelectedEvent ? scoreboardStateRef.current?.eventPartScores : undefined,
                mainEventPart: isSelectedEvent ? scoreboardStateRef.current?.mainEventPart : undefined,
              },
              messageData,
              () => {
                if (isSelectedEvent) {
                  refetchScoreboard();
                }
              }
            );

            if (isSelectedEvent) {
              setSelectedEvent(updatedEvent);

              if (
                (messageData.event === 'EVENT_PART_SCORE' && updatedEvent.eventPartScores) ||
                (messageData.event === 'EVENT' && 'statistics' in messageData.payload)
              ) {
                scoreboardUpdate = {
                  eventPartScores: updatedEvent.eventPartScores ?? scoreboardStateRef.current?.eventPartScores,
                  statistics: messageData.payload.statistics ?? scoreboardStateRef.current?.statistics,
                  mainEventPart: updatedEvent.mainEventPart ?? scoreboardStateRef.current?.mainEventPart,
                };
              }
            }
          }

          if (updatedEvent === event && !updatedEvent.eventPartScores) {
            return event;
          }

          hasEventChanges = true;
          return { ...updatedEvent, eventPartScores: undefined };
        });

        return hasEventChanges ? updatedEvents : prevState;
      });

      if (scoreboardUpdate) {
        setScoreboardState((prevState: Scoreboard | null) =>
          prevState ? { ...prevState, ...scoreboardUpdate } : prevState
        );
      }
    },
    [setEvents, setSelectedEvent, selectedEventRef, scoreboardStateRef, setScoreboardState, refetchScoreboard]
  );
};
